import { useCallback, useState } from 'react';

import { useGetPrescribedProducts } from '@/hooks';
import type { DeviceProduct, PrescriptionFormProduct } from '@/hooks/prescription/usePrescriptionForm';
import { usePostProductSurvey } from '@/hooks/rest/usePostProductSurvey';
import { useAppStore } from '@/state-management';
import type { ConsultationModel } from '@/types';
import { Logger } from '@/utils/logger';

import type { NetworkError } from '@/error-handling/types';
import { StatusCode } from '@/error-handling/types';
import type { PrescribeRequestBodySchema } from '@montugroup/prescription-contracts';
import { usePrescribePrescriptions } from '../../../hooks/prescription/usePrescribePrescriptions';
import { PrescriptionPadStep } from '../components/PrescriptionPad/PrescriptionPad.types';
import {
  transformAndMergeMedicationsAndItemsToReissue,
  transformFeedbackPayload,
  transformPrescriptionsPayload,
  transformV3DeviceProductsToV4DeviceProducts,
} from '../components/PrescriptionPad/PrescriptionPad.utils';
import { usePrescriptionAssistantTracking } from './usePrescriptionAssistantTracking';

const logger = new Logger('useConsultationTabState.ts');

export interface UseConsultationTabStateProps {
  doctorId: number;
  patientId: number;
  showConsultation: ConsultationModel | null;
}

export interface ConsultationTabState {
  /**
   * Part of `useConsultationTabState`. This function takes in the prescription state data
   * and submits it to the API to create a prescription.
   */
  submitPrescriptions: () => Promise<Response | null>;
  isPostingPrescription: boolean;
  prescribeStateError: boolean;
}

/**
 * This custom hook extracts business logic from ConsultationTab to separate
 * concerns, and allow `<ConsultationTab>` to **progressively** move towards having
 * better type safety.
 * @param root0
 * @param root0.doctorId
 * @param root0.patientId
 * @param root0.showConsultation
 */
export const useConsultationTabState = ({
  doctorId,
  patientId,
  showConsultation,
}: UseConsultationTabStateProps): ConsultationTabState => {
  const { trackPrescriptionCreated } = usePrescriptionAssistantTracking();

  const [prescribeStateError, setPrescribeStateError] = useState(false);

  const {
    prescriptionPad: { medicationPadItems, devicePadItems },
    prescriptionHistory: { itemsToReissue },
  } = useAppStore.use.prescriptionAssistant();
  const updatePrescriptionPadStep = useAppStore.use.updatePrescriptionPadStep();
  const resetPrescriptionPad = useAppStore.use.resetPrescriptionPad();
  const resetPrescriptionHistory = useAppStore.use.resetPrescriptionHistory();
  const productSurveyFeedbackPayload = transformFeedbackPayload(medicationPadItems);
  const medicationProductsPayload = transformPrescriptionsPayload(medicationPadItems);
  const { mutate: postSurvey } = usePostProductSurvey();

  const { mutateAsync: prescribePrescriptions, isPending: isPostingPrescription } = usePrescribePrescriptions();

  const { refetch: refetchPrescribedProducts } = useGetPrescribedProducts({
    patientId: `${patientId}`,
  });

  const submitPrescriptions = useCallback(async () => {
    logger.info('submitPrescriptions', {
      consultationId: showConsultation?.id,
      doctorId,
      patientId,
    });

    if (!showConsultation || !doctorId || !patientId) {
      return null;
    }

    const validProducts: (PrescriptionFormProduct | DeviceProduct)[] = medicationProductsPayload.filter(
      (product) => product.product_id !== '',
    );

    if (devicePadItems) {
      const validDeviceProducts = devicePadItems.filter((item) => !!item);
      validProducts.push(...validDeviceProducts);
    }

    // no products prescribed, skip the post
    if (validProducts.length === 0) {
      return null;
    }

    try {
      let responseBody;

      const medicationsPrescribeEndpointPayload = transformAndMergeMedicationsAndItemsToReissue(
        medicationPadItems,
        itemsToReissue,
      );

      const sendBody = {
        orderDate: new Date().toISOString(),
        products: medicationsPrescribeEndpointPayload,
        devices: transformV3DeviceProductsToV4DeviceProducts(devicePadItems),
        consultationId: showConsultation?.id,
      };

      try {
        const response = await prescribePrescriptions({
          params: {
            patientId: `${patientId}`,
          },
          body: sendBody as PrescribeRequestBodySchema,
        });
        responseBody = response.body;
      } catch (networkError: unknown) {
        if ((networkError as NetworkError).status === StatusCode.Conflict) {
          setPrescribeStateError(true);
          updatePrescriptionPadStep(PrescriptionPadStep.Create);
          resetPrescriptionPad();
          resetPrescriptionHistory();
          refetchPrescribedProducts();
        }

        throw networkError;
      }

      if (productSurveyFeedbackPayload?.length) {
        postSurvey({
          doctorId,
          patientId,
          feedback: productSurveyFeedbackPayload,
        });
      }
      // Reset form back to Create Script step when successful
      updatePrescriptionPadStep(PrescriptionPadStep.Create);
      resetPrescriptionPad();
      // refetch prescribed products on script creation
      refetchPrescribedProducts();

      if (responseBody?.prescription?.id !== undefined) {
        trackPrescriptionCreated({
          prescriptionId: responseBody.prescription.id,
          extraContext: {
            consultation: {
              consultation_id: String(showConsultation.id),
            },
          },
        });
      }

      // typescript seems unable to infer the type properly :(
      // most likely because we were using fetch in the v3 and in v4 we use useQuery
      // use type casting for now
      return responseBody as unknown as Response;
    } catch (error) {
      logger.error('Failed to create prescription', {
        doctorId,
        patientId,
        error,
      });
      throw new Error('Failed to create prescription');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId, patientId, showConsultation, medicationPadItems, devicePadItems, postSurvey]);

  return {
    submitPrescriptions,
    isPostingPrescription,
    prescribeStateError,
  };
};
